/**
 * @file 确定性 PRNG（mulberry32），种子由宿主注入。
 *
 * core 自身**不取任何随机源**（`Math.random` / `crypto` 一律禁用，见 ADR-003）。
 * 种子在 `createGame()` 时由宿主注入（服务端 `crypto.getRandomValues`、单机本地熵），
 * 之后所有随机性都由 `(seed, cursor)` 这一可序列化状态确定性推进。
 *
 * 契约：相同 `(seed, cursor)` ⇒ 相同输出；相同 `seed` + 相同 action 序列 ⇒
 * 浏览器与 Workers 上逐位一致的牌局。这是回放与对拍测试的前提。
 *
 * ⚠ 本模块为 **server-internal**：`RngState` 绝不可进任何广播事件或客户端快照
 * （泄漏它等价于泄漏整副牌墙）。
 */

import type { RngState } from './types';
import { InvariantViolationError } from './errors';

const GOLDEN = 0x9e3779b9;

/** 由 `(seed, cursor)` 推导当前 32 位状态字。 */
function stateWord(state: RngState): number {
  return (state.seed + Math.imul(state.cursor, GOLDEN)) >>> 0;
}

/**
 * 推进一次随机数，返回 `[0,1)` 浮点与游标 +1 后的新状态。纯函数。
 */
export function nextRandom(state: RngState): { value: number; state: RngState } {
  let t = (stateWord(state) + 0x6d2b79f5) | 0;
  t = Math.imul(t ^ (t >>> 15), 1 | t);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return { value, state: { algo: 'mulberry32', seed: state.seed, cursor: state.cursor + 1 } };
}

/** 用种子构造初始 `RngState`。 */
export function createRngState(seed: number): RngState {
  return { algo: 'mulberry32', seed: seed >>> 0, cursor: 0 };
}

/**
 * 抽取一个 `[0,1)` 浮点并推进游标。{@link nextRandom} 的语义化别名。
 *
 * 与被移除的 `rng.ts` 中同名函数的差异：那一版**不推进游标**（同一 state 反复调用
 * 得到同一个数），调用方必须自己记得手动 `cursor + 1`——这是隐式约定，容易漏。
 * 这里统一为「取值即推进、返回新 state」，让「忘了推进」在类型上就写不出来。
 */
export function rngFloat(state: RngState): { value: number; state: RngState } {
  return nextRandom(state);
}

/**
 * 抽取一个整数 `∈ [0, n)` 并推进游标。发牌、摸牌、洗牌都走这一条路径。
 *
 * @param n 上界（不含），必须为正整数
 * @throws {InvariantViolationError} `n` 非正整数
 */
export function rngInt(state: RngState, n: number): { value: number; state: RngState } {
  if (!Number.isInteger(n) || n <= 0) {
    throw new InvariantViolationError(`rngInt 上界必须为正整数，收到 ${n}`, { n });
  }
  const r = nextRandom(state);
  return { value: Math.floor(r.value * n), state: r.state };
}

/**
 * Fisher–Yates 洗牌（纯函数，返回新数组 + 推进后的状态）。
 * 调用方传入待洗数组与当前随机状态，产物确定性依赖于输入状态。
 */
export function shuffleInPlace<T>(
  arr: readonly T[],
  state: RngState,
): { result: T[]; state: RngState } {
  const result = arr.slice();
  let s = state;
  for (let i = result.length - 1; i > 0; i--) {
    const r = rngInt(s, i + 1);
    s = r.state;
    const j = r.value;
    const tmp = result[i]!;
    result[i] = result[j]!;
    result[j] = tmp;
  }
  return { result, state: s };
}
